import React, { useEffect } from 'react';
import { ID_INVITATION_EMAIL_OTP_BUTTON } from '../../../autoIds';
import { autoId } from '@workday/upc-common';
import { PrimaryButton } from '@workday/canvas-kit-react/button';
import { ResendOTP } from '../../../shared/resend-otp/resend-otp';
import { useEnrollmentEmailInitMutation, useEnrollmentEmailSubmitMutation } from '../../../slices/auth-api.slice';
import { useForm } from 'react-hook-form';
import styles from './invitation-verify.module.scss';
import { OTPFormField, PasscodeFormState } from '../../../shared/otp-form-field/otp-form-field';
import { useTranslation } from 'react-i18next';
import { useErrorHandling } from '../../../hooks/useErrorHandling';
import { serverError } from '../../../utils/server-error';

interface InvitationVerifyProps {
    invitation: {
        email: string;
        contactId: string;
    };
    steps: any;
}

export function InvitationVerify({ invitation, steps }: InvitationVerifyProps) {
    const { t } = useTranslation('auth');

    const { contactId, email: userEmail } = invitation;
    const { control, setError, handleSubmit } = useForm<PasscodeFormState>({
        defaultValues: {
            passcode: ''
        }
    });

    const [enrollmentEmailSubmit, enrollmentEmailSubmitMutation] = useEnrollmentEmailSubmitMutation();
    const [enrollmentEmailInit, enrollmentEmailInitMutation] = useEnrollmentEmailInitMutation();

    useEffect(() => {
        if (enrollmentEmailSubmitMutation.isSuccess) {
            steps.next();
        }
    }, [steps, enrollmentEmailSubmitMutation.isSuccess]);

    useErrorHandling(enrollmentEmailSubmitMutation.error, {
        inlineErrors: ['UPC-1005', 'UPC-1006', 'UPC-1017'],
        onInlineError: error => setError('passcode', serverError(error))
    });

    function onSubmit({ passcode }) {
        enrollmentEmailSubmit({ passcode, contactId });
    }

    function onResend() {
        enrollmentEmailInit({ contactId, showToast: true });
    }

    const disabled = enrollmentEmailSubmitMutation.isLoading || enrollmentEmailInitMutation.isLoading;

    return (
        <div>
            <h1 className={styles.title}>{t('InvitationVerify.title')}</h1>
            <p className={styles.body}>{t('InvitationVerify.description')}</p>
            <p className={styles.email}>{userEmail}</p>

            <form onSubmit={handleSubmit(onSubmit)}>
                <OTPFormField autoFocus={true} disabled={disabled} control={control} className={styles.otpFormField} />
                <PrimaryButton
                    className={styles.button}
                    disabled={disabled}
                    {...autoId(ID_INVITATION_EMAIL_OTP_BUTTON)}
                    size='large'
                    type='submit'>
                    {t('InvitationVerify.submitButton')}
                </PrimaryButton>

                <ResendOTP isDisabled={disabled} onResend={onResend} otpType='email' />
            </form>
        </div>
    );
}
